import React from 'react';
import { Paper, InternalStandardProps, Table, TablePagination, Typography } from '@mui/material';
import { withStyles, WithStyles } from '@mui/styles';
import { PaperProps } from '@mui/material/Paper';
import { SelectProps } from '@mui/material/Select';
import { TableProps } from '@mui/material/Table';
import { TableBodyProps } from '@mui/material/TableBody';
import { TableHeadProps } from '@mui/material/TableHead';
import classnames from 'classnames';
import LinkButton from '../../Button/LinkButton';
import TableListBody from './TableListBody';
import TableListHead from './TableListHead';
import { Item, Resource } from '../../../crud-graphql-containers/Utils/genericTypes';
import { stableSort, getSorting, OrderDirection } from '../../../crud-core/utils/Sort';
import { IconButtonProps } from '@mui/material/IconButton';
import { useStyles } from './styles';

export interface TableEvents {
  onSort?: (orderBy: string, order: OrderDirection) => void;
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (rowsPerpage: number) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLElement>, checked: boolean) => void;
  onSelectClick?: (event: React.MouseEvent<HTMLTableRowElement>, id: string) => void;
}

interface TablePaginationProps {
  backIconButtonProps?: Partial<IconButtonProps>;
  nextIconButtonProps?: Partial<IconButtonProps>;
  labelRowsPerPage?: string;
  rowsPerPageOptions?: number[];
  SelectProps?: SelectProps;
}

export type TableListProps = {
  bulkSelectionPossible?: boolean;
  children?: React.ReactNode;
  items?: Item[];
  itemCount?: number;
  labelSortTooltip?: string;
  order?: OrderDirection;
  orderBy?: string;
  page?: number;
  paperProps?: PaperProps;
  resourceConfig?: Resource;
  rowsPerPage?: number;
  tableProps?: TableProps;
  tableHeadProps?: TableHeadProps;
  tableBodyProps?: TableBodyProps;
  tablePaginationProps?: TablePaginationProps;
  faButtonRoute?: string;
  faButtonIcon?: JSX.Element;
  listTitle?: string;
} & TableEvents &
  WithStyles<any> &
  InternalStandardProps<React.HTMLAttributes<HTMLDivElement>>;

interface TableListState {
  order?: OrderDirection;
  orderBy?: string;
  selected: string[];
  page?: number;
  rowsPerPage?: number;
}

class TableList extends React.Component<TableListProps, TableListState> {
  state: TableListState = { selected: [] };

  componentDidMount() {
    const { order, orderBy, page, rowsPerPage } = this.props;
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ order, orderBy, page, rowsPerPage });
  }

  handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const { items = [], resourceConfig: { keyProperty = 'id' } = {} } = this.props;
    if (event.target.checked) {
      this.setState({ selected: items.map((item: any) => item[keyProperty]) });
      return;
    }

    if (this.props.onSelectAllClick) {
      this.props.onSelectAllClick(event, checked);
    }

    this.setState({ selected: [] });
  };

  handleClick = (event: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    if (this.props.onSelectClick) {
      this.props.onSelectClick(event, id);
    }

    this.setState({ selected: newSelected });
  };

  handleRequestSort = (event: React.MouseEvent<HTMLElement>, orderBy: string) => {
    let order: OrderDirection = OrderDirection.DESC;

    if (this.state.orderBy === orderBy && this.state.order === OrderDirection.DESC) {
      order = OrderDirection.ASC;
    }

    if (this.props.onSort) {
      this.props.onSort(orderBy, order);
    }

    this.setState({ order, orderBy });
  };

  handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    if (this.props.onChangePage) {
      this.props.onChangePage(page);
    }

    this.setState({ page });
  };

  handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const rowsPerpage = parseInt(event.target.value, 10);
    if (this.props.onChangeRowsPerPage) {
      this.props.onChangeRowsPerPage(rowsPerpage);
    }
    this.setState({ rowsPerPage: rowsPerpage, page: 0 });
  };

  render() {
    const {
      children,
      classes,
      className,
      items = [],
      itemCount,
      onSelectClick,
      onSelectAllClick,
      order: orderFromProps,
      orderBy: orderByFromProps,
      paperProps,
      style,
      tablePaginationProps,
      tableProps,
      faButtonRoute,
      faButtonIcon,
      listTitle,
      ...rest
    } = this.props;
    const {
      order = orderFromProps,
      orderBy = orderByFromProps,
      page = 0,
      rowsPerPage = 25,
      selected,
    } = this.state;

    const sortedItems = (orderBy ? stableSort(items, getSorting(order, orderBy)) : items).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    // const emptyRows = rowsPerPage - Math.min(rowsPerPage, items.length - page * rowsPerPage);

    return (
      <>
        <Paper {...paperProps} className={classnames(classes.root, className)} style={style}>
          {listTitle && (
            <Typography variant="h6" className={classes.listTitle}>
              {listTitle}
            </Typography>
          )}
          <div className={classes.tableWrapper}>
            <Table className={classes.table} {...tableProps}>
              <TableListHead
                items={sortedItems}
                onRequestSort={this.handleRequestSort}
                onSelectAllClick={this.handleSelectAllClick}
                selected={selected}
                order={order}
                orderBy={orderBy}
                {...rest}
              >
                {children}
              </TableListHead>
              <TableListBody
                items={sortedItems}
                onSelectClick={this.handleClick}
                selected={selected}
                {...rest}
              >
                {children}
              </TableListBody>
            </Table>
          </div>
          <TablePagination
            component="div"
            count={itemCount || items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              'aria-label': 'Previous Page',
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page',
            }}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            {...tablePaginationProps}
          />
        </Paper>
        {faButtonRoute && (
          <LinkButton
            to={faButtonRoute}
            buttonProps={{ color: 'primary' }}
            floatingButton={true}
            className={classes.faButton}
            icon={faButtonIcon}
          />
        )}
      </>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default withStyles(useStyles as any)(TableList);
